/* ──────────────────────────────────────────────────────────────
   NotificacionesPage.tsx – lista paginada de notifs IN-APP
   ────────────────────────────────────────────────────────────── */
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  Toolbar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tabs,
  Tab,
  TextField,
  Checkbox,
  FormGroup,
  Autocomplete,
  Snackbar,
  Alert,
} from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FiberIcon from "@mui/icons-material/FiberManualRecord";

import api from "../api/axios";
import { NOTIF_LABELS } from "../constants/enums";
import { useAuth } from "../context/AuthContext";

/* —— tipos de backend —— */
interface ReservaPendiente {
  id: number;
  fecha: string;
  hora_inicio: string;
  profesor: string;
  alumno: string;
}
interface Notif {
  id: number;
  event: string;
  payload: Record<string, any>;
  readAt: string | null;
  createdAt: string;
  /** opcionales: el backend puede enviarlas para resaltar/filtrar */
  priority?: "P0" | "P1" | "P2";
  requiresAction?: boolean;
}
interface NotifResp {
  rows: Notif[];
  total: number;
}

/* paginación */
const TAKE = 20;
type TabKey = "important" | "pending" | "all";

const ROLE_OPTIONS = [
  "profesor",
  "admin",
  "manager",
  "att_familias",
  "att_profesorado",
  "att_alumnado",
  "facturacion",
  "hr",
  "admisiones",
  "consultor",
  "cliente",
];

export default function NotificacionesPage() {
  const { t } = useTranslation<"translation">();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [rows, setRows] = useState<Notif[]>([]);
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoad] = useState(false);
  const [onlyUnread, setUnread] = useState(false);
  const [tab, setTab] = useState<TabKey>("important");

  /* — diálogo digest — */
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgRows, setDlgRows] = useState<ReservaPendiente[]>([]);
  const openDigestDlg = (r: ReservaPendiente[]) => {
    setDlgRows(r);
    setDlgOpen(true);
  };

  /* — diálogo broadcast (solo admin/hr) — */
  const canBroadcast = user?.roles?.some((r: string) => ["admin", "hr"].includes(r));
  const [massOpen, setMassOpen] = useState(false);
  const [bcTitle, setBcTitle] = useState("");
  const [bcBody, setBcBody] = useState("");
  const [bcInApp, setBcInApp] = useState(true);
  const [bcEmail, setBcEmail] = useState(false);
  const [bcRoles, setBcRoles] = useState<string[]>([]);
  const [bcWhen, setBcWhen] = useState<string>(""); // datetime-local
  const [bcSending, setBcSending] = useState(false);
  const [snack, setSnack] = useState<{
    open: boolean;
    msg: string;
    type: "success" | "error";
  }>({
    open: false,
    msg: "",
    type: "success",
  });

  /* —— fetch paginado —— */
  const load = async (reset = false) => {
    if (!user) return;
    setLoad(true);
    try {
      const { data } = await api.get<NotifResp>("/notifications", {
        params: { skip: reset ? 0 : skip, take: TAKE, unread: onlyUnread, tab },
      });
      setRows((r) => (reset ? data.rows : [...r, ...data.rows]));
      setSkip((s) => (reset ? data.rows.length : s + data.rows.length));
      setTotal(data.total);
    } finally {
      setLoad(false);
    }
  };
  useEffect(() => {
    load(true); /*eslint-disable-next-line*/
  }, [onlyUnread, tab]);

  /* —— helpers marcar leído —— */
  const markRead = async (id: number) => {
    await api.patch(`/notifications/${id}/read`);
    setRows((prev) =>
      prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n))
    );
  };
  const markAllRead = async () => {
    await api.patch("/notifications/mark-all-read");
    setRows((prev) => prev.map((n) => ({ ...n, readAt: new Date().toISOString() })));
  };

  /* —— envío masivo —— */
  const handleBroadcastSend = async () => {
    if (!bcTitle.trim() || !bcBody.trim() || (!bcInApp && !bcEmail) || bcRoles.length === 0) {
      setSnack({
        open: true,
        msg: "Completa título, mensaje, canales y roles.",
        type: "error",
      });
      return;
    }
    setBcSending(true);
    try {
      await api.post("/notifications/broadcasts", {
        title: bcTitle.trim(),
        body: bcBody,
        channels: [bcInApp && "IN_APP", bcEmail && "EMAIL"].filter(Boolean),
        audience: { roles: bcRoles },
        scheduledAt: bcWhen || undefined,
      });
      setSnack({
        open: true,
        msg: "Broadcast programado/enviado.",
        type: "success",
      });
      setMassOpen(false);
      // reset sencillo
      setBcTitle("");
      setBcBody("");
      setBcInApp(true);
      setBcEmail(false);
      setBcRoles([]);
      setBcWhen("");
    } catch (e: any) {
      setSnack({
        open: true,
        msg: "No se pudo enviar el broadcast.",
        type: "error",
      });
    } finally {
      setBcSending(false);
    }
  };

  /* —— click en item —— */
  const handleItemClick = async (n: Notif) => {
    await markRead(n.id);

    /* ①  Documentos */
    if (n.event === "DOC_AVAILABLE" || n.event === "DOC_PENDING_SIGNATURE") {
      if (n.event === "DOC_PENDING_SIGNATURE" && n.payload?.signUrl) {
        window.open(n.payload.signUrl, "_blank", "noopener,noreferrer");
      } else {
        /* ruta según rol */
        if (user?.roles?.includes("profesor")) navigate("/profesor/documentos");
        else if (user?.roles?.includes("cliente"))
          navigate("/cliente/documentos"); // ajusta si existe
        else navigate("/admin/manager/documentos");
      }
      return;
    }

    /* ②  Digest semanal de pendientes */
    if (n.event === "PENDING_REBOOK_WEEKLY_DIGEST" && n.payload?.reservas) {
      openDigestDlg(n.payload.reservas);
      return;
    }

    /* ③  Vacaciones / ausencias (solo HR) */
    if (
      ["VAC_REQ_CREATED", "VAC_REQ_BATCH_CREATED", "ABS_REQ_CREATED"].includes(n.event) &&
      n.payload?.profId &&
      user?.roles?.includes("hr")
    ) {
      navigate("/admin/manager/profesor-vacaciones", {
        state: { profId: n.payload.profId, batchId: n.payload.batchId },
      });
      return;
    }

    /* ④  Broadcast: no navegamos; ya quedó marcada como leída */
    if (n.event === "BROADCAST") {
      return;
    }

    /* ⑤  Deep‑link genérico (p.ej. FIRMA_PENDIENTE_ALUMNO → abrir formulario) */
    const href = n.payload?.href ?? n.payload?.formURL ?? n.payload?.url;
    if (href) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }

    /* …otros tipos futuros… */
  };

  /* sin sesión → nada que mostrar */
  if (!user) return null;

  /* ═════════════════════ render ═════════════════════ */
  return (
    <Box sx={{ maxWidth: 920, mx: "auto" }}>
      <Toolbar />

      {/* encabezado */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {t("notificaciones.titulo", "Notificaciones")}
        </Typography>

        {canBroadcast && (
          <Button variant="contained" onClick={() => setMassOpen(true)}>
            {t("notificaciones.envioMasivo", "Envío masivo")}
          </Button>
        )}

        <IconButton
          title={t<string>("notificaciones.btn.marcarTodo", "Marcar todo como leído")}
          onClick={markAllRead}
        >
          <DoneAllIcon />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v: TabKey) => setTab(v)} sx={{ mb: 1 }}>
        <Tab value="important" label={t("notifs.tab.important", "Importantes")} />
        <Tab value="pending" label={t("notifs.tab.pending", "Pendientes")} />
        <Tab value="all" label={t("notifs.tab.all", "Todas")} />
      </Tabs>

      <FormControlLabel
        control={<Switch checked={onlyUnread} onChange={(_, v) => setUnread(v)} />}
        label={t("notificaciones.filtro.soloNoLeidas", "Solo no leídas")}
        sx={{ mb: 1 }}
      />

      {/* listado */}
      <List>
        {rows.map((n) => {
          const unread = n.readAt === null;

          // ⚠️ la fecha en payload puede venir YA como DD/MM/YYYY
          const rawFecha = n.payload?.fecha as string | undefined;
          const fechaFmt =
            typeof rawFecha === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(rawFecha)
              ? rawFecha
              : rawFecha
                ? dayjs(rawFecha).format("DD/MM/YYYY")
                : undefined;

          const primary =
            n.event === "BROADCAST"
              ? ((n.payload?.title as string) ?? t("notifications.broadcast.title", "Aviso"))
              : String(
                  t(NOTIF_LABELS[n.event] ?? n.event, {
                    ...n.payload,
                    fecha: fechaFmt,
                  })
                );

          const secondary =
            n.event === "BROADCAST" && n.payload?.body
              ? String(n.payload.body)
              : dayjs(n.createdAt).format("D/M/YYYY • HH:mm");

          return (
            <Box key={n.id}>
              <ListItem
                button
                onClick={() => handleItemClick(n)}
                secondaryAction={
                  unread && (
                    <IconButton
                      edge="end"
                      title={t<string>("notificaciones.btn.marcarUna", "Marcar como leída")}
                      onClick={(e) => {
                        e.stopPropagation();
                        markRead(n.id);
                      }}
                    >
                      <DoneAllIcon fontSize="small" />
                    </IconButton>
                  )
                }
                sx={{
                  bgcolor: unread ? "action.selected" : undefined,
                  // resaltar P0 si viene informado
                  borderLeft: n.priority === "P0" ? "3px solid" : undefined,
                  borderLeftColor: n.priority === "P0" ? "error.main" : undefined,
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {unread && <FiberIcon sx={{ fontSize: 10 }} />}
                      <span>{primary}</span>
                    </Box>
                  }
                  secondary={secondary}
                />
              </ListItem>
              <Divider component="li" />
            </Box>
          );
        })}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </List>

      {rows.length < total && (
        <Box sx={{ textAlign: "center", my: 2 }}>
          <Button variant="outlined" onClick={() => load()}>
            {t("notificaciones.btn.cargarMas", "Cargar más")}
          </Button>
        </Box>
      )}

      {/* diálogo digest semanal */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t("notificaciones.resumenPendientes", "Clases pendientes de reagendar")}
        </DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>{t("tabla.fecha", "Fecha")}</TableCell>
                <TableCell>{t("tabla.hora", "Hora")}</TableCell>
                <TableCell>{t("tabla.profesor", "Profesor")}</TableCell>
                <TableCell>{t("tabla.alumno", "Alumno")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dlgRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {t("notificaciones.sinPendientes", "No hay pendientes")}
                  </TableCell>
                </TableRow>
              )}
              {dlgRows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.id}</TableCell>
                  <TableCell>{dayjs(r.fecha).format("DD/MM/YYYY")}</TableCell>
                  <TableCell>{r.hora_inicio.slice(0, 5)}</TableCell>
                  <TableCell>{r.profesor}</TableCell>
                  <TableCell>{r.alumno}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {/* diálogo envío masivo */}
      <Dialog open={massOpen} onClose={() => setMassOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t("notificaciones.broadcast.titulo", "Enviar notificación masiva")}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label={t("notificaciones.broadcast.tituloCampo", "Título")}
            fullWidth
            value={bcTitle}
            onChange={(e) => setBcTitle(e.target.value)}
          />
          <TextField
            label={t("notificaciones.broadcast.mensajeCampo", "Mensaje")}
            fullWidth
            multiline
            minRows={4}
            value={bcBody}
            onChange={(e) => setBcBody(e.target.value)}
            sx={{ mt: 2 }}
          />
          <FormGroup row sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={bcInApp} onChange={(_, v) => setBcInApp(v)} />}
              label="In-app"
            />
            <FormControlLabel
              control={<Checkbox checked={bcEmail} onChange={(_, v) => setBcEmail(v)} />}
              label="Email"
            />
          </FormGroup>
          <Autocomplete
            multiple
            options={ROLE_OPTIONS}
            value={bcRoles}
            onChange={(_, v) => setBcRoles(v)}
            renderInput={(p) => (
              <TextField {...p} label={t("notificaciones.broadcast.roles", "Roles destino")} />
            )}
            sx={{ mt: 1 }}
          />
          <TextField
            label={t("notificaciones.broadcast.programar", "Programar (opcional)")}
            type="datetime-local"
            value={bcWhen}
            onChange={(e) => setBcWhen(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1 }}
          />

          <Box sx={{ textAlign: "right", mt: 2 }}>
            <Button
              onClick={handleBroadcastSend}
              variant="contained"
              disabled={
                bcSending ||
                !bcTitle.trim() ||
                !bcBody.trim() ||
                (!bcInApp && !bcEmail) ||
                bcRoles.length === 0
              }
            >
              {bcSending ? t("comun.enviando", "Enviando…") : t("comun.enviar", "Enviar")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.type} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
